import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { HOME } from "@/constants/testIds";
import SectionTitle from "@/components/SectionTitle";

const SECTOR_ICONS = {
  "Strategy": "fa-chess-knight", "Communications": "fa-chess-knight",
  "Digital News": "fa-bolt", "Media": "fa-bolt",
  "AI": "fa-microchip", "Think Tank": "fa-microchip", "Policy": "fa-microchip",
  "Hearing": "fa-headphones", "Healthcare": "fa-headphones", "Audio": "fa-headphones",
  "Payments": "fa-indian-rupee-sign", "Fintech": "fa-indian-rupee-sign",
  "Live Entertainment": "fa-microphone", "Comedy": "fa-microphone",
  "Sports IP": "fa-baseball-bat-ball",
  "F&B": "fa-utensils", "Casual Dining": "fa-utensils",
};
function sectorIcon(sector) {
  if (!sector) return "fa-briefcase";
  for (const k of Object.keys(SECTOR_ICONS)) {
    if (sector.includes(k)) return SECTOR_ICONS[k];
  }
  return "fa-briefcase";
}

export default function Home() {
  const [profile, setProfile] = useState(null);
  const [news, setNews] = useState([]);
  const [videos, setVideos] = useState([]);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, n, v, c] = await Promise.all([
        client.get("/profile"),
        client.get("/news", { params: { limit: 6 } }),
        client.get("/videos", { params: { limit: 3 } }),
        client.get("/companies"),
      ]);
      setProfile(p.data); setNews(n.data.items); setVideos(v.data.items); setCompanies(c.data.items);
    })();
  }, []);

  return (
    <main data-testid={HOME.hero}>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 chess-pattern opacity-50 pointer-events-none"></div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 lg:gap-16 relative">
          <div className="lg:col-span-7 fade-up">
            <div className="eyebrow mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[#b8975d]"></span>
              <span>Office of the President · All India Chess Federation</span>
            </div>
            <h1 className="serif-title text-[clamp(2.5rem,6vw,5.5rem)] mb-6 text-[#0a1628]">
              Nitin Narang
            </h1>
            <p className="font-display text-[clamp(1.25rem,2.2vw,1.75rem)] italic text-[#2c2c2c] mb-2 leading-snug">
              President, All India Chess Federation.
            </p>
            <p className="font-body text-[15px] tracking-[0.2em] uppercase text-[#6b6b6b] mb-10">
              Entrepreneur &nbsp;·&nbsp; Investor &nbsp;·&nbsp; Ecosystem Builder
            </p>
            <p className="text-[17px] leading-[1.8] text-[#2c2c2c] max-w-xl mb-6">
              {profile?.short_bio || "Loading…"}
            </p>
            {profile?.hero_narrative && (
              <div className="border-l-2 border-[#b8975d] pl-5 mb-10 max-w-xl">
                <p className="font-display text-[18px] italic text-[#0a1628] leading-snug">
                  {profile.hero_narrative}
                </p>
              </div>
            )}
            <div className="flex flex-wrap gap-4">
              <Link to="/about" data-testid={HOME.ctaBio} className="btn">
                Read Biography <i className="fa-solid fa-arrow-right"></i>
              </Link>
              <Link to="/speaking" data-testid={HOME.ctaSpeaking} className="btn btn-ghost">Book a Talk</Link>
              <Link to="/media-kit" data-testid={HOME.ctaMediaKit} className="btn btn-ghost">Media Kit</Link>
              <Link to="/news" data-testid={HOME.ctaNews} className="btn btn-ghost">Latest News</Link>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up delay-2">
            <div className="relative">
              {/* Editorial collage — Nitin only */}
              <div className="grid grid-cols-12 grid-rows-6 gap-3 h-[640px]">
                {/* Large anchor — official portrait */}
                <div className="col-span-7 row-span-6 relative overflow-hidden border hairline bg-[#ece7da]">
                  {profile?.hero_image && (
                    <img src={profile.hero_image} alt="Nitin Narang" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/55 via-transparent to-transparent pointer-events-none"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-[#faf8f3]">
                    <div className="eyebrow text-[#b8975d] mb-0.5 text-[10px]">President · AICF</div>
                    <div className="font-display text-sm">Official portrait · 2024</div>
                  </div>
                </div>
                {/* Right column — three Nitin moments */}
                <div className="col-span-5 row-span-2 relative overflow-hidden border hairline bg-[#ece7da]">
                  <img src="https://www.fide.com/wp-content/uploads/WC-Closing-Narang.jpg" alt="Nitin Narang speaking — FIDE World Cup 2025 closing" className="w-full h-full object-cover"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/70 via-transparent to-transparent pointer-events-none"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-2.5 text-[#faf8f3]">
                    <div className="eyebrow text-[#b8975d] mb-0.5 text-[9px]">Speaking · Goa 2025</div>
                    <div className="font-display text-[12px]">FIDE World Cup Closing</div>
                  </div>
                </div>
                <div className="col-span-5 row-span-2 relative overflow-hidden border hairline bg-[#ece7da]">
                  <img src="https://www.fide.com/wp-content/uploads/WC-2025-OPening-Modi-Message.jpg" alt="Nitin Narang delivering PM Modi's message" className="w-full h-full object-cover"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/70 via-transparent to-transparent pointer-events-none"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-2.5 text-[#faf8f3]">
                    <div className="eyebrow text-[#b8975d] mb-0.5 text-[9px]">Opening Ceremony</div>
                    <div className="font-display text-[12px]">Delivering PM Modi's message</div>
                  </div>
                </div>
                <div className="col-span-5 row-span-2 relative overflow-hidden border hairline bg-[#ece7da]">
                  <img src="https://www.fide.com/wp-content/uploads/WC-2025-Opening-FRONT.jpg" alt="Nitin Narang on stage — FIDE World Cup 2025 opening" className="w-full h-full object-cover"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/70 via-transparent to-transparent pointer-events-none"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-2.5 text-[#faf8f3]">
                    <div className="eyebrow text-[#b8975d] mb-0.5 text-[9px]">On Stage · Goa</div>
                    <div className="font-display text-[12px]">World Cup Opening 2025</div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-5 -right-5 bg-[#0a1628] text-[#faf8f3] px-5 py-3 text-[11px] tracking-[0.22em] uppercase z-10">
                <span className="text-[#b8975d]">est.</span> &nbsp; March 2024
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10"><div className="rule"></div></div>

      {/* FEATURED IN — press strip */}
      {profile?.featured_in?.length > 0 && (
        <section data-testid={HOME.featuredIn} className="bg-white border-y hairline">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
            <div className="flex items-center gap-6 flex-wrap justify-center">
              <span className="text-[10px] tracking-[0.32em] uppercase text-[#6b6b6b] mr-4">As featured in</span>
              {profile.featured_in.map((p, i) => (
                <a key={i} href={p.url} target="_blank" rel="noreferrer"
                  className="font-display text-[15px] lg:text-[17px] text-[#0a1628] hover:text-[#b8975d] transition-colors italic">
                  {p.name}
                  {i < profile.featured_in.length - 1 && <span className="ml-6 text-[#d9d4c7] not-italic">·</span>}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* NARRATIVE PILLARS */}
      {profile?.pillars?.length > 0 && (
        <section data-testid={HOME.pillars} className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">The story · in four pillars</div>
              <h2 className="serif-title text-4xl lg:text-5xl text-[#0a1628] leading-tight">
                A public institution.<br/><span className="italic text-[#b8975d]">A founder's portfolio.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-6">
              <p className="text-[#4a4a4a] text-[16px] leading-[1.8]">
                Nitin Narang's work sits at the intersection of national institution-building and India-scale entrepreneurship — chess administration, AI governance, Hindi digital news for Bharat, live entertainment and a portfolio of more than twenty investments across the consumer-tech economy.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {profile.pillars.map((p, i) => (
              <div key={i} data-testid={HOME.pillar(i)} className="border hairline bg-white p-7 card-hover flex flex-col">
                <div className="w-11 h-11 mb-5 border hairline flex items-center justify-center text-[#b8975d] bg-[#faf8f3]">
                  <i className={`fa-solid ${p.icon || 'fa-compass'}`}></i>
                </div>
                <div className="eyebrow text-[10px] text-[#b8975d] mb-2">{String(i + 1).padStart(2, '0')} · {p.kicker}</div>
                <h3 className="font-display text-xl text-[#0a1628] mb-3 leading-tight">{p.title}</h3>
                <p className="text-[13px] text-[#4a4a4a] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10"><div className="rule"></div></div>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid={HOME.featuredChess}>
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">Chess Leadership</div>
            <h2 className="serif-title text-4xl lg:text-5xl mb-6 text-[#0a1628]">
              An institution<br/><span className="italic text-[#b8975d]">for the next two decades.</span>
            </h2>
            <p className="text-[#2c2c2c] text-[16px] leading-[1.8] mb-6">
              From Gukesh's World Championship to India's double-gold at the Chess Olympiad — the AICF's blueprint, <em>Checkmating Challenges for Chess Development in Bharat</em>, anchors player welfare, an India-first rating ecosystem, and a hosting calendar that brought the FIDE World Cup back to India after 23 years.
            </p>
            <Link to="/chess-leadership" className="link-underline text-[14px] tracking-[0.18em] uppercase font-medium">
              Explore Chess Vision →
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { stat: "₹65 cr", label: "Development budget for Indian chess" },
              { stat: "10 lakh", label: "Target players on the India rating system" },
              { stat: "206 / 82", label: "Players from countries at the 2025 FIDE World Cup, Goa" },
              { stat: "2024–27", label: "Presidential term · AICF" },
            ].map((s, i) => (
              <div key={i} className="border hairline p-7 card-hover bg-white">
                <div className="font-display text-4xl mb-2 text-[#0a1628]">{s.stat}</div>
                <div className="text-[13px] text-[#6b6b6b] leading-relaxed">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPANIES */}
      <section className="bg-white border-y hairline" data-testid={HOME.featuredCompanies}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
          <SectionTitle eyebrow="Ventures & Investments" title="A second-generation founder's portfolio."
            kicker="Strategy & comms, AI policy, digital news, live entertainment, payments, audio tech and F&B — built or backed by Nitin Narang across more than a decade of public and private enterprise."/>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {companies.map(c => (
              <Link key={c.slug} to={`/companies/${c.slug}`}
                className="border hairline bg-[#faf8f3] p-6 card-hover group flex flex-col">
                <div className="h-10 mb-4 flex items-center justify-between">
                  {c.logo ? (
                    <img src={c.logo} alt={c.name} className="h-9 max-w-[140px] object-contain" onError={(e)=>{e.currentTarget.style.display='none'}}/>
                  ) : (
                    <div className="w-10 h-10 border hairline flex items-center justify-center text-[#b8975d]">
                      <i className={`fa-solid ${sectorIcon(c.sector)}`}></i>
                    </div>
                  )}
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#b8975d]">#{String(c.order).padStart(2,'0')}</span>
                </div>
                <div className="eyebrow text-[10px] mb-2">{c.sector}</div>
                <div className="font-display text-lg mb-2 text-[#0a1628] leading-tight">{c.name}</div>
                <div className="text-[12px] text-[#6b6b6b] mb-3 flex-1">{c.role}</div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#0a1628] flex items-center gap-2 group-hover:gap-3 transition-all border-t hairline pt-3">
                  Profile <i className="fa-solid fa-arrow-right text-[9px]"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST NEWS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid={HOME.latestNews}>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
          <div>
            <div className="eyebrow mb-3">In the news</div>
            <h2 className="serif-title text-4xl lg:text-5xl text-[#0a1628]">Newsroom</h2>
          </div>
          <Link to="/news" className="link-underline text-[13px] tracking-[0.18em] uppercase font-medium">All Coverage →</Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {news.slice(0, 6).map((n, i) => (
            <a key={n.id} href={n.url} target="_blank" rel="noreferrer"
              className={`border hairline bg-white p-7 card-hover block ${i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`}>
              <div className="flex items-center justify-between mb-5">
                <span className="eyebrow text-[#b8975d]">{n.category}</span>
                <span className="text-[11px] text-[#6b6b6b]">{new Date(n.date).toLocaleDateString("en-IN", {month: "short", day: "numeric", year: "numeric"})}</span>
              </div>
              <h3 className={`font-display text-[#0a1628] mb-3 ${i === 0 ? "text-3xl lg:text-4xl leading-tight" : "text-xl leading-snug"}`}>
                {n.title}
              </h3>
              <p className="text-[#4a4a4a] text-[14px] leading-relaxed mb-5">{n.excerpt}</p>
              <div className="flex items-center justify-between pt-4 border-t hairline">
                <span className="text-[11px] tracking-[0.2em] uppercase text-[#6b6b6b]">{n.source}</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[#b8975d] text-xs"></i>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* LATEST VIDEOS */}
      <section className="section-dark relative overflow-hidden" data-testid={HOME.latestVideos}>
        <div className="absolute inset-0 chess-pattern opacity-[0.04] pointer-events-none"></div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 relative">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
            <div>
              <div className="eyebrow mb-3">Speeches, interviews & media</div>
              <h2 className="serif-title text-4xl lg:text-5xl text-[#faf8f3]">In conversation.</h2>
            </div>
            <Link to="/videos" className="text-[13px] tracking-[0.18em] uppercase font-medium text-[#b8975d] hover:underline">All Videos →</Link>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {videos.map(v => (
              <Link key={v.id} to={`/videos`} className="block group">
                <div className="relative aspect-video overflow-hidden bg-[#0a1628] border border-[rgba(255,255,255,0.1)]">
                  <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"/>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 border border-[#b8975d] flex items-center justify-center bg-[rgba(10,22,40,0.6)] backdrop-blur">
                      <i className="fa-solid fa-play text-[#b8975d]"></i>
                    </div>
                  </div>
                </div>
                <div className="pt-5">
                  <div className="eyebrow text-[#b8975d] mb-2">{v.category}</div>
                  <h3 className="font-display text-lg text-[#faf8f3] leading-snug mb-2">{v.title}</h3>
                  <div className="text-[12px] text-[#9d9580]">{v.channel}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRESS CONTACT */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="border hairline bg-white p-10 lg:p-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-3">Press & speaking</div>
            <h3 className="serif-title text-3xl lg:text-4xl text-[#0a1628] mb-4">For media enquiries and speaking invitations.</h3>
            <p className="text-[#4a4a4a] text-[15px] leading-relaxed max-w-xl">
              The office of Shri Nitin Narang accepts media enquiries, speaking invitations and partnership requests through the contact channels below.
            </p>
          </div>
          <div className="lg:col-span-5 space-y-3">
            <a href="mailto:nitin@theideazfactory.com" className="flex items-center gap-4 border hairline p-4 hover:border-[#0a1628] transition-colors">
              <i className="fa-solid fa-envelope text-[#b8975d]"></i>
              <span className="text-[14px]">nitin@theideazfactory.com</span>
            </a>
            <a href="https://wa.me/919988884393" target="_blank" rel="noreferrer" className="flex items-center gap-4 border hairline p-4 hover:border-[#0a1628] transition-colors">
              <i className="fa-brands fa-whatsapp text-[#b8975d]"></i>
              <span className="text-[14px]">+91 99888 84393</span>
            </a>
            <Link to="/contact" className="btn w-full justify-center">Open Contact Form</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
